import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
const EMPTY_SOURCES: readonly Blob[] = [];
type ObjectUrlMap = ReadonlyMap<Blob, string>;
function getObjectUrlApi(): typeof globalThis.URL | null {
  const urlApi = globalThis.URL;
  if (typeof urlApi?.createObjectURL !== "function" || typeof urlApi.revokeObjectURL !== "function") {
    return null;
  }
  return urlApi;
}
export function useObjectUrl(source) {
  const $ = _c(2);
  let t0;
  if ($[0] !== source) {
    t0 = source ? [source] : EMPTY_SOURCES;
    $[0] = source;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const sources = t0;
  const objectUrls = useObjectUrls(sources);
  return source ? objectUrls[0] ?? null : null;
}
export function useObjectUrls(sources: readonly Blob[]): readonly (string | null)[] {
  const [urlBySource, setUrlBySource] = useState<ObjectUrlMap>(() => new Map());
  const latestUrlBySource = useRef<ObjectUrlMap>(urlBySource);
  useEffect(() => {
    latestUrlBySource.current = urlBySource;
  }, [urlBySource]);
  useEffect(() => {
    const urlApi = getObjectUrlApi();
    if (!urlApi) {
      if (urlBySource.size) {
        const emptyUrls = new Map<Blob, string>();
        latestUrlBySource.current = emptyUrls;
        // Object URLs must be created after commit; this state publishes the ready URLs.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setUrlBySource(emptyUrls);
      }
      return;
    }
    const activeSources = new Set(sources);
    let nextUrls: Map<Blob, string> | null = null;
    const getNextUrls = () => {
      nextUrls ??= new Map(urlBySource);
      return nextUrls;
    };
    for (const [source, url] of urlBySource) {
      if (!activeSources.has(source)) {
        urlApi.revokeObjectURL(url);
        getNextUrls().delete(source);
      }
    }
    for (const source_0 of sources) {
      if (!(nextUrls ?? urlBySource).has(source_0)) {
        getNextUrls().set(source_0, urlApi.createObjectURL(source_0));
      }
    }
    if (nextUrls) {
      const resolvedUrls = nextUrls;
      latestUrlBySource.current = resolvedUrls;
      // Object URLs must be created after commit; this state publishes the ready URLs.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUrlBySource(resolvedUrls);
    }
  }, [sources, urlBySource]);
  useEffect(() => () => {
    const urlApi_0 = getObjectUrlApi();
    if (!urlApi_0) {
      return;
    }
    for (const url_0 of latestUrlBySource.current.values()) {
      urlApi_0.revokeObjectURL(url_0);
    }
    latestUrlBySource.current = new Map();
  }, []);
  return sources.map(source_1 => urlBySource.get(source_1) ?? null);
}